import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import { useParams } from 'react-router-dom';
import {
  StyledInvoicePageContainer,
  StyledInvoicePageContainerSpace,
} from './StyledInvoicePage';
import NotFoundPage from '../NotFoundPage';
import PageContainer from '../../components/PageContainer';
import PageDocumentDetails from '../../components/PageDocumentDetails';
import SpinSmall from '../../components/SpinSmall';
import BalancePageHeader from '../../components/pages/TransactionsPage/BalancePageHeader/BalancePageHeader';
import InvoiceDocumentCard from '../../components/pages/InvoicePage/InvoiceDocumentCard';
import InvoiceOverview from '../../components/pages/InvoicePage/InvoiceOverview';
import { invoicesActions } from '../../state/actions/invoice.actions';
import routes from '../../routes/routes.json';
import { objectHelpers, systemHelpers } from '../../utils/helpers';
import { firebaseEvents } from '../../snippets/firebase';


const InvoicePage = ({
  invoicesListUrl,
  getInvoice,
  updateInvoice,
}) => {
  const [invoice, setInvoice] = useState(null);
  const [pageLoading, setPageLoading] = useState({loading: true, isExist: true});

  const [t] = useTranslation(['main']);

  const uriParams = useParams();

  const invoiceId = useMemo(() => uriParams?.invoiceId, [uriParams]);

  const {breadcrumbs, documentTitle} = useMemo(() => {
    const createdDate = invoice?.created_at;
    const title = !pageLoading?.isExist && !pageLoading?.loading
      ? t('elementNotFound', { name: t('invoice') })
      : createdDate
        ? moment(createdDate).format('DD MMM YYYY')
        : '';

    return {
      breadcrumbs: [
        {title: t('navigation.invoices'), href: invoicesListUrl || routes.invoicesList},
        {title}
      ],
      documentTitle: `${t('pageTitles.invoiceDetails')} ${title ?? ''}`,
    }
  }, [invoice, invoicesListUrl, t, pageLoading]);

  const finishLoading = (isExist = true) => setPageLoading({...pageLoading, isExist, loading: false});

  useEffect(() => {
    if (!objectHelpers.isValidValue(invoiceId)) return;

    setPageLoading({...pageLoading, loading: true});

    getInvoice(
      invoiceId,
      (invoice) => {
        setInvoice(invoice);
        finishLoading();
      },
      () => finishLoading(false)
    )
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditInvoice = ({data, successCallback, errorCallback} = {}) => {
    if (!invoice?.id) return;

    updateInvoice(
      invoice.id,
      data,
      (updatedInvoice) => {
        setInvoice({...updatedInvoice, file_url: invoice.file_url})
        successCallback?.(updatedInvoice);
        systemHelpers.logEvent(firebaseEvents.INVOICES_EDIT_INVOICE_DETAILS);
      },
      (resp) => errorCallback?.(resp)
    )
  }

  const handleUpdateInvoice = (invoice) => {
    if (invoice) {
      setInvoice(invoice);
    } else {
      getInvoice(invoiceId, (invoice) => setInvoice(invoice));
    }
  }

  const {isExist, loading} = pageLoading;

  return (
    <PageContainer>
      <PageDocumentDetails title={documentTitle} />
      <BalancePageHeader breadcrumbs={breadcrumbs} />
      <SpinSmall spinning={loading}>
        <StyledInvoicePageContainer>

          {isExist ? (
            <StyledInvoicePageContainerSpace
              align='start'
              size='large'
            >
              <InvoiceDocumentCard invoice={invoice} />
              <InvoiceOverview
                invoice={invoice}
                onEdit={handleEditInvoice}
                onUpdate={handleUpdateInvoice}
              />
            </StyledInvoicePageContainerSpace>
          ) : !loading && <NotFoundPage />}

        </StyledInvoicePageContainer>
      </SpinSmall>
    </PageContainer>
  );
}

const mapStateToProps = ({ invoice: { invoicesListUrl }}) => ({
  invoicesListUrl
});

const mapDispatchToProps = {
  getInvoice: invoicesActions.getInvoice,
  updateInvoice: invoicesActions.updateInvoice
}

export default connect(mapStateToProps, mapDispatchToProps)(InvoicePage);
